import { Stomp, type IMessage } from '@stomp/stompjs'
import { useCallback, useEffect, useState } from 'react'
import {
  getWebSocketUrl,
  showAlert,
  SituacaoGravacaoType,
} from '../../../../../../_metronic/helpers'
import { useAuth } from '../../../../auth'
import { getTeleAuth } from '../../../../tele-consulta/core/teleConsultaAuth'
import { iniciarGravacao, listarGravacoes, pararGravacao } from '../core/_requests'

const SITUACOES_ATIVAS: SituacaoGravacaoType[] = [
  SituacaoGravacaoType.Solicitada,
  SituacaoGravacaoType.Gravando,
]
const SITUACOES_GRAVACAO: SituacaoGravacaoType[] = [
  SituacaoGravacaoType.Solicitada,
  SituacaoGravacaoType.Gravando,
  SituacaoGravacaoType.Encerrando,
  SituacaoGravacaoType.Disponivel,
  SituacaoGravacaoType.Erro,
]

const RECONNECT_DELAY_MS = 5000

const isAtiva = (situacao: SituacaoGravacaoType): boolean => SITUACOES_ATIVAS.includes(situacao)

const isSituacaoGravacao = (valor: string): valor is SituacaoGravacaoType =>
  (SITUACOES_GRAVACAO as string[]).includes(valor)

export const useGravacao = (idAtendimento: number, isPaciente = false) => {
  const { auth } = useAuth()
  const [isGravacaoAtiva, setIsGravacaoAtiva] = useState(false)
  const [gravacaoPendente, setGravacaoPendente] = useState(false)

  useEffect(() => {
    let cancelado = false
    listarGravacoes(idAtendimento, isPaciente)
      .then((gravacoes) => {
        if (!cancelado) {
          setIsGravacaoAtiva(gravacoes.some((gravacao) => isAtiva(gravacao.situacao)))
        }
      })
      .catch((error) => {
        if (!cancelado) {
          console.error(
            'useGravacao: falha ao consultar o histórico de gravações do atendimento',
            idAtendimento,
            error
          )
        }
      })
    return () => {
      cancelado = true
    }
  }, [idAtendimento, isPaciente])

  useEffect(() => {
    const accessToken = isPaciente ? getTeleAuth()?.accessToken : auth?.accessToken
    if (!accessToken) return

    const stompClient = Stomp.client(getWebSocketUrl('/socket/atendimento'))
    stompClient.debug = () => undefined
    stompClient.reconnect_delay = RECONNECT_DELAY_MS
    let reconexao = false

    const onConnect = () => {
      // Eventos publicados enquanto o socket esteve fora se perderam: ressincroniza pela API.
      if (reconexao) {
        listarGravacoes(idAtendimento, isPaciente)
          .then((gravacoes) =>
            setIsGravacaoAtiva(gravacoes.some((gravacao) => isAtiva(gravacao.situacao)))
          )
          .catch(() => undefined)
      }
      reconexao = true
      stompClient.subscribe(`/topic/atendimento/gravacao/${idAtendimento}`, (message: IMessage) => {
        const situacao = message.body

        if (isSituacaoGravacao(situacao)) {
          setIsGravacaoAtiva(isAtiva(situacao))
        }
      })
    }

    stompClient.connect({ Authorization: `Bearer ${accessToken}` }, onConnect, () => undefined)

    return () => {
      try {
        stompClient.disconnect()
      } catch {}
    }
  }, [idAtendimento, isPaciente, auth?.accessToken])

  const toggleGravacao = useCallback(async () => {
    if (gravacaoPendente) {
      return
    }
    setGravacaoPendente(true)
    const estadoAnterior = isGravacaoAtiva
    try {
      const gravacao = estadoAnterior
        ? await pararGravacao(idAtendimento)
        : await iniciarGravacao(idAtendimento)
      setIsGravacaoAtiva(gravacao !== null && isAtiva(gravacao.situacao))
    } catch {
      setIsGravacaoAtiva(estadoAnterior)
      showAlert({
        title: 'Erro!',
        text: estadoAnterior ? 'Erro ao parar a gravação.' : 'Erro ao iniciar a gravação.',
        icon: 'error',
      })
    } finally {
      setGravacaoPendente(false)
    }
  }, [gravacaoPendente, idAtendimento, isGravacaoAtiva])

  return { isGravacaoAtiva, gravacaoPendente, toggleGravacao }
}
